<template>
  <div>
    <div class="mb-2 flex flex-wrap gap-2">
      <button class="btn btn-sm btn-outline" @click="zoom(1.25)" title="Phóng to">
        <i class="fa-solid fa-magnifying-glass-plus"></i>
      </button>
      <button class="btn btn-sm btn-outline" @click="zoom(0.8)" title="Thu nhỏ">
        <i class="fa-solid fa-magnifying-glass-minus"></i>
      </button>
      <button class="btn btn-sm btn-outline" @click="fit()" title="Vừa màn hình">
        <i class="fa-solid fa-expand"></i>
      </button>
      <button
        class="btn btn-sm btn-outline"
        :class="{ 'btn-active': physicsOn }"
        @click="togglePhysics()"
        title="Bật/tắt layout vật lý"
      >
        <i class="fa-solid fa-atom"></i>
      </button>
      <div class="divider divider-horizontal mx-0"></div>
      <div class="dropdown dropdown-end">
        <button class="btn btn-sm btn-outline" @click="exportOpen = !exportOpen">
          <i class="fa-solid fa-download mr-1"></i>Export
        </button>
        <ul v-if="exportOpen" tabindex="-1" class="dropdown-content menu bg-base-100 rounded-box z-10 p-2 shadow-sm w-44">
          <li><a @click="doExport('png'); exportOpen = false"><i class="fa-solid fa-image mr-1"></i>PNG</a></li>
          <li><a @click="doExport('json'); exportOpen = false"><i class="fa-solid fa-file-code mr-1"></i>JSON</a></li>
          <li><a @click="doExport('graphml'); exportOpen = false"><i class="fa-solid fa-diagram-project mr-1"></i>GraphML</a></li>
          <li><a @click="doExport('drawio'); exportOpen = false"><i class="fa-solid fa-draw-polygon mr-1"></i>Draw.io</a></li>
          <li><a @click="doExport('csv'); exportOpen = false"><i class="fa-solid fa-file-csv mr-1"></i>CSV</a></li>
        </ul>
      </div>
    </div>

    <div ref="container" class="network-topology-canvas" />
  </div>
</template>

<script setup lang="ts">
import type { Edge, Network } from "vis-network";
import { Network as VisNetwork } from "vis-network";
import { DataSet } from "vis-data";
import { nextTick, ref, watch } from "vue";
import {
  buildDrawioXML,
  buildGraphJSON,
  buildGraphML,
  buildGraphCSV,
  downloadText,
  subnetOf,
  type GraphEdge,
  type GraphNode,
} from "./exportGraph";

export interface NetworkNode {
  ip: string;
  doc_id?: string;
  agent_id?: string;
  scan_id?: string;
  role?: string; // host | router | switch | subnet
  os?: string; // windows | linux | macos | unknown
  hostname?: string;
  critical?: boolean;
}

const props = defineProps<{
  data: NetworkNode[];
  edges?: GraphEdge[];
}>();

const emit = defineEmits<{
  (e: "node-click", node: NetworkNode): void;
}>();

const container = ref<HTMLElement | null>(null);
let network: Network | null = null;
let currentNodes: GraphNode[] = [];
let currentEdges: GraphEdge[] = [];
const physicsOn = ref(true);
const exportOpen = ref(false);

// FontAwesome is loaded app-wide via index.html CDN; canvas labels can use it
// once the webfont is loaded (document.fonts). Glyphs: windows/linux/apple.
const OS_GLYPH: Record<string, string> = {
  windows: "\u{f17a}",
  linux: "\u{f17c}",
  macos: "\u{f17f}",
};
const FA_FONT = '"Font Awesome 7 Free", "Font Awesome 6 Free", sans-serif';

const ROLE_STYLE: Record<
  string,
  { shape: string; background: string; border: string }
> = {
  router: { shape: "triangle", background: "#4338ca", border: "#a5b4fc" },
  switch: { shape: "hexagon", background: "#0369a1", border: "#7dd3fc" },
  subnet: { shape: "diamond", background: "#4338ca", border: "#a5b4fc" },
  host: { shape: "box", background: "#475569", border: "#94a3b8" },
  critical: { shape: "star", background: "#b91c1c", border: "#fca5a5" },
};

const render = async (data: NetworkNode[]) => {
  if (!container.value) return;
  if (network) {
    network.destroy();
    network = null;
  }


  // Group by /24 subnet for the hierarchy layer. Nodes without a usable IPv4
  // are rendered as plain top-level nodes (no fabricated parent).
  const subnetMap = new Map<string, NetworkNode[]>();
  const ungrouped: NetworkNode[] = [];
  for (const item of data) {
    const key = subnetOf(item.ip);
    if (!key) {
      ungrouped.push(item);
      continue;
    }
    if (!subnetMap.has(key)) subnetMap.set(key, []);
    subnetMap.get(key)!.push(item);
  }

  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];

  // One mapping used by both the grouped and the ungrouped case, so a node's
  // rendering cannot drift between the two paths.
  const toGraphNode = (n: NetworkNode): GraphNode => ({
    id: n.ip,
    label: n.ip,
    role: n.role || "host",
    os: n.os || "unknown",
    hostname: n.hostname,
    critical: n.critical,
  });

  for (const [subnet, ips] of subnetMap) {
    nodes.push({
      id: subnet,
      label: subnet,
      role: "subnet",
      os: "unknown",
    });
    for (const ip of ips) {
      nodes.push(toGraphNode(ip));
      edges.push({ id: `${subnet}--${ip.ip}`, from: subnet, to: ip.ip });
    }
  }

  // Non-IPv4 nodes: shown as top-level nodes with no subnet parent, rather than
  // being dropped or bucketed under a made-up subnet.
  for (const n of ungrouped) {
    nodes.push(toGraphNode(n));
  }

  // Host-to-host edges from the backend (agent host -> target).
  for (const e of props.edges || []) {
    edges.push(e);
  }

  currentNodes = nodes;
  currentEdges = edges;

  const visNodes = nodes.map((n) => {
    const style = n.critical ? ROLE_STYLE.critical : ROLE_STYLE[n.role] || ROLE_STYLE.host;
    const glyph = OS_GLYPH[n.os] || "";
    const label = glyph ? `${glyph} ${n.label}` : n.label;
    return {
      id: n.id,
      label,
      title: [
        `${n.role.toUpperCase()}: ${n.label}`,
        n.hostname ? `Hostname: ${n.hostname}` : "",
        n.os && n.os !== "unknown" ? `OS: ${n.os}` : "",
        n.critical ? "CRITICAL" : "",
      ]
        .filter(Boolean)
        .join("\n"),
      shape: style.shape,
      size: n.critical ? 30 : style.shape === "diamond" ? 30 : 25,
      color: {
        background: style.background,
        border: style.border,
        highlight: { background: style.background, border: "#ffffff" },
        hover: { background: style.background, border: "#ffffff" },
      },
      font: {
        size: 14,
        color: "#ffffff",
        face: glyph ? FA_FONT : "monospace",
      },
    };
  });

  // Static curves with identical endpoints/roundness overlap, including labels.
  // Group both directions so reverse edges cannot reuse the same physical lane.
  const parallelEdges = new Map<string, GraphEdge[]>();
  for (const edge of edges) {
    const key = JSON.stringify([edge.from, edge.to].sort());
    if (!parallelEdges.has(key)) parallelEdges.set(key, []);
    parallelEdges.get(key)!.push(edge);
  }
  const edgeCurves = new Map<string, Edge["smooth"]>();
  for (const group of parallelEdges.values()) {
    if (group.length < 2) continue;
    group.forEach((edge, index) => {
      // ponytail: finite canvas space; dense groups need aggregation, not tighter lanes.
      const offset = (index - (group.length - 1) / 2) * Math.min(0.3, 2 / group.length);
      edgeCurves.set(edge.id, {
        enabled: true,
        type: (edge.from < edge.to ? offset : -offset) < 0 ? "curvedCCW" : "curvedCW",
        roundness: Math.abs(offset),
      });
    });
  }

  const visEdges = edges.map((e) => ({
    id: e.id,
    smooth: edgeCurves.get(e.id),
    from: e.from,
    to: e.to,
    label: e.port || undefined,
    font: {
      size: 12,
      color: "#e2e8f0",
      background: "#1e293b",
      strokeWidth: 2,
      strokeColor: "#0f172a",
      align: "horizontal" as const,
    },
    arrows: e.id.includes("--") && !e.port ? undefined : { to: true },
  }));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const visData: any = {
    nodes: new DataSet(visNodes),
    edges: new DataSet(visEdges),
  };
  network = new VisNetwork(container.value, visData, {
    nodes: { borderWidth: 2, shadow: true },
    edges: {
      width: 1.5,
      color: "#94a3b8",
      smooth: { enabled: true, type: "curvedCW", roundness: 0.15 },
    },
    physics: {
      enabled: true,
      solver: "forceAtlas2Based",
      forceAtlas2Based: {
        gravitationalConstant: -120,
        centralGravity: 0.01,
        springLength: 120,
        springConstant: 0.05,
      },
      stabilization: { enabled: true, iterations: 200 },
    },
    interaction: {
      hover: true,
      tooltipDelay: 100,
      zoomView: true,
      dragView: true,
      dragNodes: true,
    },
  });

  network.on("stabilizationIterationsDone", () => {
    if (physicsOn.value) network?.setOptions({ physics: { enabled: false } });
  });

  network.on("click", (params: { nodes?: (string | number)[] }) => {
    const clicked = params.nodes?.[0];
    if (clicked === undefined) return;
    const node = props.data.find((n) => n.ip === clicked);
    if (node) emit("node-click", node);
  });
};

const zoom = (scale: number) => {
  if (!network) return;
  const s = network.getScale() * scale;
  network.moveTo({ scale: s, animation: { duration: 200, easingFunction: "easeInOutQuad" } });
};

const fit = () => {
  network?.fit({ animation: { duration: 300, easingFunction: "easeInOutQuad" } });
};

const togglePhysics = () => {
  physicsOn.value = !physicsOn.value;
  network?.setOptions({ physics: { enabled: physicsOn.value } });
};

const doExport = (format: "png" | "json" | "graphml" | "drawio" | "csv") => {
  const stamp = new Date().toISOString().slice(0, 10);
  if (format === "png") {
    const canvas = container.value?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `network-topology-${stamp}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }
  if (format === "json") {
    downloadText(buildGraphJSON(currentNodes, currentEdges), `network-topology-${stamp}.json`, "application/json");
  } else if (format === "graphml") {
    downloadText(buildGraphML(currentNodes, currentEdges), `network-topology-${stamp}.graphml`, "application/xml");
  } else if (format === "csv") {
    downloadText(buildGraphCSV(currentNodes, currentEdges), `network-topology-${stamp}.csv`, "text/csv");
  } else {
    downloadText(buildDrawioXML(currentNodes, currentEdges), `network-topology-${stamp}.drawio`, "application/xml");
  }
};

watch(
  () => props.data,
  async () => {
    await nextTick();
    await render(props.data);
  },
  { deep: true, immediate: true },
);
</script>

<style scoped>
.network-topology-canvas {
  height: 600px;
  background: #0f172a;
}
</style>
